import React, { useState } from 'react';
import { useAFStore } from '../stores/data/afStore';
import CopyButton from './CopyButton';
import { api } from '../api';
import { formatMinutesToLargestUnit } from '../utils/formatters';
import SingleBetRankChart from './SingleBetRankChart';

// 정렬 가능한 컬럼 정의
const SORTABLE_COLUMNS = [
  'tokenAmount',
  'avgBuyMC', 
  'netBettingRank',
  'avgHoldingTime',
  'pnl',
  'winRate',
  'scalpingRatio',
  'mcRange0',
  'mcRange1', 
  'mcRange2',
  'mcRange3',
  'mcRange4'
] as const;

type SortableColumn = typeof SORTABLE_COLUMNS[number];

// 컬럼 정의
const COLUMNS = [
  { key: 'address', label: 'Wallet', width: 'w-[160px]', align: 'left', sortable: false },
  { key: 'twitter', label: 'Twitter', width: 'w-[150px]', align: 'left', sortable: false },
  { key: 'identity', label: 'Identity', width: 'w-[100px]', align: 'center', sortable: false },
  { key: 'pattern', label: 'Pattern', width: 'w-[110px]', align: 'center', sortable: false },
  { key: 'performance', label: 'Performance', width: 'w-[110px]', align: 'center', sortable: false },
  { key: 'tokenAmount', label: 'Token Amount', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'avgBuyMC', label: 'Avg Entry MC', width: 'w-[100px]', align: 'center', sortable: true },
  { key: 'netBettingRank', label: 'Net Rank', width: 'w-[100px]', align: 'center', sortable: true },
  { key: 'avgHoldingTime', label: 'Avg Holding Duration', width: 'w-[100px]', align: 'center', sortable: true },
  { key: 'pnl', label: 'PnL', width: 'w-[90px]', align: 'center', sortable: true },
  { key: 'winRate', label: 'Win Rate', width: 'w-[60px]', align: 'center', sortable: true },
  { key: 'scalpingRatio', label: 'Scalping Ratio', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'mcRange0', label: '0-100k', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'mcRange1', label: '100k-300k', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'mcRange2', label: '300k-1m', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'mcRange3', label: '1m-5m', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'mcRange4', label: '5m+', width: 'w-[80px]', align: 'center', sortable: true },
  { key: 'copy', label: '', width: 'w-[24px]', align: 'center', sortable: false }
];

interface AFListProps {
  addresses: string[];
  title?: string;
  showCount?: boolean;
  maxHeight?: string;
  tokenAddress?: string; // 토큰 주소 추가
  alphaFinderMetrics?: any; // TokenDetailModal에서 전달받는 데이터
  evaluationResults?: any; // TokenDetailModal에서 전달받는 데이터
  isLoading?: boolean; // 로딩 상태
  addressTokenAmounts: Array<{ address: string; tokenAmount: number }>; // addressTokenAmounts 추가
  afDetails?: Array<{ // afDetails 추가
    address: string;
    identity: string;
    pattern: string[];
    performance: string;
    twitterFollowers: number;
    twitterName: string;
    twitterProfilePicture: string;
  }>;
}

const AFList: React.FC<AFListProps> = ({
  addresses,
  title = "AF Wallet List",
  showCount = true,
  maxHeight = "max-h-40",
  tokenAddress,
  alphaFinderMetrics: externalAlphaFinderMetrics,
  evaluationResults: externalEvaluationResults,
  isLoading: externalIsLoading = false,
  addressTokenAmounts,
  afDetails = []
}) => {
  const { getLegendaryAddresses } = useAFStore();
  const legendaryAddresses = getLegendaryAddresses();
  const isLegendaryAddress = (address: string) => legendaryAddresses.includes(address);
  // TokenDetailModal에서 전달받은 데이터 사용 (없으면 null)
  const alphaFinderMetrics = externalAlphaFinderMetrics || null;
  const evaluationResults = externalEvaluationResults || null;
  const isLoading = externalIsLoading;
  
  // Expanded 상태 관리
  const [expandedAddress, setExpandedAddress] = useState<string | null>(null);
  const [expandedData, setExpandedData] = useState<any>(null);
  const [isLoadingExpanded, setIsLoadingExpanded] = useState(false);

  // 정렬 상태 관리
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // alphaFinderMetrics에서 address -> avgBuyPrice 매핑 생성
  const getAvgBuyPrice = (address: string) => {
    if (!alphaFinderMetrics || Object.keys(alphaFinderMetrics).length === 0) return null;
    const afData = alphaFinderMetrics[address];
    return afData?.avgBuyPrice || null;
  };

  // alphaFinderMetrics에서 address -> netBettingRank 매핑 생성
  const getNetBettingRank = (address: string) => {
    if (!alphaFinderMetrics || Object.keys(alphaFinderMetrics).length === 0) return null;
    const afData = alphaFinderMetrics[address];
    return afData?.netBettingRank || null;
  };

  // evaluationResults에서 address -> evaluation data 매핑 생성
  const getEvaluationData = (address: string) => {
    if (!evaluationResults?.results) return null;
    return evaluationResults.results[address] || null;
  };

  // afDetails에서 address -> af detail 매핑 생성
  const getAfDetail = (address: string) => {
    if (!afDetails || afDetails.length === 0) return null;
    return afDetails.find(af => af.address === address) || null;
  };

  const totalTokenAmount = addressTokenAmounts.length > 0 
    ? addressTokenAmounts.reduce((sum, item) => sum + item.tokenAmount, 0)
    : 0;
  
  // 토큰 보유량을 가중치로 사용한 가중 Avg Entry MC 계산
  const averageAvgBuyMC = (() => {
    if (addresses.length === 0 || totalTokenAmount === 0) return 0;
    
    // 각 AF의 avgBuyMC * tokenAmount의 합계 계산
    const weightedSum = addresses.reduce((sum, address) => {
      const avgBuyMC = getAvgBuyPrice(address) || 0;
      const addressTokenAmount = addressTokenAmounts.find(
        item => item.address === address
      );
      const tokenAmount = addressTokenAmount?.tokenAmount || 0;
      return sum + (avgBuyMC * tokenAmount);
    }, 0);
    
    // 가중 평균 = Σ(avgBuyMC_i * tokenAmount_i) / Σ(tokenAmount_i)
    return weightedSum / totalTokenAmount;
  })();

  // 토큰 보유량 Top 5의 평균 Net Rank %와 Avg Entry MC 계산
  const top5ByTokenAmount = (() => {
    if (addresses.length === 0) return { avgNetRank: null, avgBuyMC: null };
    
    // 토큰 보유량 기준으로 정렬
    const sortedByTokenAmount = [...addresses]
      .map(address => {
        const tokenAmount = addressTokenAmounts.find(item => item.address === address)?.tokenAmount || 0;
        return { address, tokenAmount };
      })
      .sort((a, b) => b.tokenAmount - a.tokenAmount)
      .slice(0, 5); // Top 5 (5명이 안되면 전체)
    
    if (sortedByTokenAmount.length === 0) return { avgNetRank: null, avgBuyMC: null };
    
    // Net Rank % 수집
    const netRanks: number[] = [];
    
    // 보유량 가중치를 고려한 매수 MC 계산
    let weightedSumMC = 0;
    let totalTokenAmount = 0;
    
    sortedByTokenAmount.forEach(({ address, tokenAmount }) => {
      const netBettingRank = getNetBettingRank(address);
      if (netBettingRank && netBettingRank.rank && netBettingRank.totalCnt) {
        const netRankPercent = (netBettingRank.rank / netBettingRank.totalCnt) * 100;
        netRanks.push(netRankPercent);
      }
      
      const avgBuyMC = getAvgBuyPrice(address);
      if (avgBuyMC !== null && avgBuyMC !== undefined && tokenAmount > 0) {
        weightedSumMC += avgBuyMC * tokenAmount;
        totalTokenAmount += tokenAmount;
      }
    });
    
    const avgNetRank = netRanks.length > 0 
      ? netRanks.reduce((sum, val) => sum + val, 0) / netRanks.length 
      : null;
    
    // 가중 Avg Entry MC = Σ(avgBuyMC_i × tokenAmount_i) / Σ(tokenAmount_i)
    const avgBuyMC = totalTokenAmount > 0 
      ? weightedSumMC / totalTokenAmount 
      : null;
    
    return { avgNetRank, avgBuyMC };
  })();

  // PnL Top 5의 평균 Net Rank %와 Avg Entry MC 계산
  const top5ByPnL = (() => {
    if (addresses.length === 0) return { avgNetRank: null, avgBuyMC: null };
    
    // PnL 기준으로 정렬
    const sortedByPnL = [...addresses]
      .map(address => {
        const evaluationData = getEvaluationData(address);
        const pnl = evaluationData?.metrics?.pnl || 0;
        return { address, pnl };
      })
      .sort((a, b) => b.pnl - a.pnl)
      .slice(0, 5); // Top 5 (5명이 안되면 전체)
    
    if (sortedByPnL.length === 0) return { avgNetRank: null, avgBuyMC: null };
    
    // Net Rank % 수집
    const netRanks: number[] = [];
    
    // 보유량 가중치를 고려한 매수 MC 계산
    let weightedSumMC = 0;
    let totalTokenAmount = 0;
    
    sortedByPnL.forEach(({ address }) => {
      const netBettingRank = getNetBettingRank(address);
      if (netBettingRank && netBettingRank.rank && netBettingRank.totalCnt) {
        const netRankPercent = (netBettingRank.rank / netBettingRank.totalCnt) * 100;
        netRanks.push(netRankPercent);
      }
      
      const avgBuyMC = getAvgBuyPrice(address);
      const tokenAmount = addressTokenAmounts.find(item => item.address === address)?.tokenAmount || 0;
      
      if (avgBuyMC !== null && avgBuyMC !== undefined && tokenAmount > 0) {
        weightedSumMC += avgBuyMC * tokenAmount;
        totalTokenAmount += tokenAmount;
      }
    });
    
    const avgNetRank = netRanks.length > 0 
      ? netRanks.reduce((sum, val) => sum + val, 0) / netRanks.length 
      : null;
    
    // 가중 Avg Entry MC = Σ(avgBuyMC_i × tokenAmount_i) / Σ(tokenAmount_i)
    const avgBuyMC = totalTokenAmount > 0 
      ? weightedSumMC / totalTokenAmount 
      : null;
    
    return { avgNetRank, avgBuyMC };
  })();

  // 각 컬럼별 정렬 비교 함수
  const getSortValue = (address: string, column: SortableColumn): number => {
    const tokenAmount = addressTokenAmounts.find(item => item.address === address)?.tokenAmount || 0;
    const avgBuyMC = getAvgBuyPrice(address) || 0;
    const netBettingRank = getNetBettingRank(address);
    const evaluationData = getEvaluationData(address);

    switch (column) {
      case 'tokenAmount':
        return tokenAmount;
      
      case 'avgBuyMC':
        return avgBuyMC;
      
      case 'netBettingRank':
        if (!netBettingRank) return 0;
        return (netBettingRank.rank / netBettingRank.totalCnt) * 100;
      
      case 'avgHoldingTime':
        return evaluationData?.metrics?.avg_holding_time_in_min || 0;
      
      case 'pnl':
        return evaluationData?.metrics?.pnl || 0;
      
      case 'winRate':
        return evaluationData?.metrics?.win_rate || 0;
      
      case 'scalpingRatio':
        if (!evaluationData?.metrics?.scalping_cnt || !evaluationData?.metrics?.total_invest_cnt) return 0;
        return (evaluationData.metrics.scalping_cnt / evaluationData.metrics.total_invest_cnt) * 100;
      
      case 'mcRange0':
      case 'mcRange1':
      case 'mcRange2':
      case 'mcRange3':
      case 'mcRange4':
        const rangeIndex = parseInt(column.replace('mcRange', ''));
        return evaluationData?.metrics?.mc_range_metrics?.[rangeIndex]?.count || 0;
      
      default:
        return 0;
    }
  };

  // 헤더 클릭 핸들러
  const handleHeaderClick = (columnKey: string) => {
    if (!SORTABLE_COLUMNS.includes(columnKey as SortableColumn)) return;

    if (sortColumn === columnKey) {
      // 같은 컬럼 클릭 시 방향 토글
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 컬럼 클릭 시 해당 컬럼으로 변경하고 내림차순으로 시작
      setSortColumn(columnKey);
      setSortDirection('desc');
    }
  };

  // MC Range Count 기반 색상 계산 함수
  const getMcRangeTextColor = (evaluationData: any, currentRangeIndex: number) => {
    if (!evaluationData?.metrics?.mc_range_metrics) return '';

    const mcRangeMetrics = evaluationData.metrics.mc_range_metrics;
    
    // 현재 셀의 count 값
    const currentMetric = mcRangeMetrics[currentRangeIndex];
    if (!currentMetric || currentMetric.count === undefined || currentMetric.count === null) return '';
    
    const currentCount = parseInt(currentMetric.count);
    
    // 모든 mc range의 count 값들을 수집하여 최대값 찾기
    const allCounts = mcRangeMetrics
      .map((metric: any) => {
        if (!metric || metric.count === undefined || metric.count === null) return 0;
        return parseInt(metric.count);
      })
      .filter((count: number) => count > 0);
    
    if (allCounts.length === 0) return '';
    
    const maxCount = Math.max(...allCounts);
    
    // 현재 셀이 최대 count를 가진 경우에만 초록색
    if (currentCount === maxCount && currentCount > 0) {
      return 'text-green-400 font-semibold'; // 진한 초록색 텍스트
    }
    
    return ''; // 기본 색상 (무색)
  };

  // 데이터 정렬 (사용자 정렬 또는 기본 우선순위 정렬)
  const sortedAddresses = [...addresses].sort((a, b) => {
    // 사용자가 정렬을 선택한 경우
    if (sortColumn && SORTABLE_COLUMNS.includes(sortColumn as SortableColumn)) {
      const aValue = getSortValue(a, sortColumn as SortableColumn);
      const bValue = getSortValue(b, sortColumn as SortableColumn);
      
      if (sortDirection === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    }

    // 기본 우선순위 정렬 (정렬이 선택되지 않은 경우)
    const aNetBettingRank = getNetBettingRank(a);
    const aEvaluationData = getEvaluationData(a);
    const bNetBettingRank = getNetBettingRank(b);
    const bEvaluationData = getEvaluationData(b);

    // 우선순위 계산 (높을수록 우선)
    const getPriority = (netBettingRank: any, evaluationData: any) => {
      if (netBettingRank && evaluationData) return 3; // 둘 다 있음
      if (netBettingRank) return 2; // netBettingRank만 있음
      if (evaluationData) return 1; // evaluationData만 있음
      return 0; // 둘 다 없음
    };

    const aPriority = getPriority(aNetBettingRank, aEvaluationData);
    const bPriority = getPriority(bNetBettingRank, bEvaluationData);

    // 우선순위가 높은 순으로 정렬
    if (aPriority !== bPriority) {
      return bPriority - aPriority;
    }

    // 같은 우선순위일 때는 Legendary AF 우선
    const aIsLegendary = isLegendaryAddress(a);
    const bIsLegendary = isLegendaryAddress(b);

    if (aIsLegendary && !bIsLegendary) return -1;
    if (!aIsLegendary && bIsLegendary) return 1;
    return 0;
  });

  const handleGmgnClick = (address: string) => {
    const link = `https://gmgn.ai/sol/address/${address}`;
    window.open(link, '_blank');
  };

  const handleSolscanClick = (address: string) => {
    const link = `https://solscan.io/address/${address}`;
    window.open(link, '_blank');
  };

  // 행 클릭 핸들러
  const handleRowClick = async (address: string) => {
    // 같은 행을 클릭한 경우 expanded 상태 토글
    if (expandedAddress === address) {
      setExpandedAddress(null);
      setExpandedData(null);
      return;
    }

    // 다른 행을 클릭한 경우
    setExpandedAddress(address);
    setExpandedData(null);
    setIsLoadingExpanded(true);

    try {
      if (tokenAddress) {
        const detailData = await api.getTokenAlphaFinderMetricDetail(tokenAddress, address);
        setExpandedData(detailData);
      }
    } catch (error) {
      console.error('❌ getTokenAlphaFinderMetricDetail API 호출 실패:', error);
      setExpandedData(null);
    } finally {
      setIsLoadingExpanded(false);
    }
  };

  // Expanded 영역 렌더링 함수
  const renderExpandedContent = () => {
    if (!expandedAddress) return null;

    return (
      <div className="border-t border-gray-600 py-4 px-12" style={{ backgroundColor: '#191919' }}>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-white">
          </h4>
          <button
            onClick={() => {
              setExpandedAddress(null);
              setExpandedData(null);
            }}
            className="text-gray-400 font-semibold hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>
        
        {isLoadingExpanded ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-400">Loading...</div>
          </div>
        ) : expandedData ? (
          <div className="space-y-3 pb-4">
            {/* 랭킹 정보 */}
            {expandedData.ranks && (
              <div className="flex gap-4 justify-between">
                {/* Single Bet Rank - 차트로 표시 */}
                <div className="w-1/2 border border-gray-600 p-1">
                  <div className="text-sm font-semibold text-white mb-3 text-center">Single Buy Rank Timeline</div>
                  {expandedData.ranks.singleBetRanks && Array.isArray(expandedData.ranks.singleBetRanks) && expandedData.ranks.singleBetRanks.length > 0 ? (
                    <SingleBetRankChart 
                      data={expandedData.ranks.singleBetRanks}
                      width={550}
                      height={400}
                      title=""
                    />
                  ) : (
                    <div className="text-gray-400 text-sm text-center py-8">데이터 없음</div>
                  )}
                </div>

                {/* Cumulative Buy Rank - MC Range별로 표시 */}
                {expandedData.ranks.cumulativeBuyRanks && Array.isArray(expandedData.ranks.cumulativeBuyRanks) && (
                  <div className="w-1/2 border border-gray-600 p-4">
                    <div className="text-sm font-semibold text-white mb-3 text-center">Cumulative Buy Rank</div>
                    
                    {/* 헤더 */}
                    <div className="flex justify-between items-center py-1 px-2 text-xs text-gray-400 border-b border-gray-600 mb-2">
                      <div className="w-[80px] text-center">0-100k</div>
                      <div className="w-[80px] text-center">100k-300k</div>
                      <div className="w-[80px] text-center">300k-1m</div>
                      <div className="w-[80px] text-center">1m-5m</div>
                      <div className="w-[80px] text-center">5m+</div>
                    </div>
                    
                    {/* 데이터 행 */}
                    <div className="flex justify-between items-center py-1">
                      {['0-100k', '100k-300k', '300k-1m', '1m-5m', '5m+'].map((range, index) => {
                        const rangeData = expandedData.ranks.cumulativeBuyRanks.find(
                          (item: any) => item.mcRangeTier === range
                        );
                        
                        return (
                          <div key={range} className="w-[80px] text-center">
                            {rangeData ? (
                              <div className="text-gray-300 text-xs">
                                <div className={`font-semibold ${
                                  (() => {
                                    const rankPercentage = (rangeData.rank / rangeData.totalCnt) * 100;
                                    if (rankPercentage <= 10 || rangeData.rank === 1) {
                                      return 'text-green-400';
                                    } else if (rankPercentage >= 90) {
                                      return 'text-red-400';
                                    }
                                    return 'text-gray-300';
                                  })()
                                }`}>
                                  #{rangeData.rank} <span className='text-gray-500 text-[10px]'>/ {rangeData.totalCnt}</span>
                                </div>
                                <div className="text-gray-400 text-[10px] mt-1">
                                  (${Number(rangeData.value.toFixed(0)).toLocaleString()})
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-500 text-xs">-</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

          
              </div>
            )}

            {/* 추가 메트릭스 정보 */}
            {Object.keys(expandedData).length > 3 && (
              <div className="border border-gray-500 rounded p-3">
                <div className="text-sm font-semibold text-white mb-2">추가 정보</div>
                <div className="text-xs text-gray-400">
                  <pre className="whitespace-pre-wrap overflow-x-auto">
                    {JSON.stringify(expandedData, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400">데이터를 불러올 수 없습니다.</div>
          </div>
        )}
      </div>
    );
  };

  // 컬럼 데이터 렌더링 함수
  const renderColumnData = (column: any, address: string, tokenAmount: number, avgBuyMC: number, netBettingRank: any, evaluationData: any) => {
    const baseClasses = `${column.width} text-${column.align}`;

    switch (column.key) {
      case 'address':
        return (
          <div className={`${baseClasses} pl-2`}>
            <div className="flex items-center space-x-2">
              <span className={`font-mono text-xs ${isLegendaryAddress(address) ? 'text-amber-500' : 'text-gray-300'}`}>
                {address.slice(0, 4)}...{address.slice(-4)}
              </span>
              <div className="flex space-x-1">
                <button
                  className="w-4 h-4 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGmgnClick(address);
                  }}
                >
                  <img src="/gmgn.png" alt="gmgn" className="w-4 h-4" />
                </button>
                <button
                  className="w-4 h-4 rounded-sm flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSolscanClick(address);
                  }}
                >
                  <img src="/solscan.png" alt="solscan" className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );

      case 'twitter':
        const afDetail = getAfDetail(address);
        if (!afDetail) return <div className={baseClasses}>-</div>;

        return (
          <div className={`${baseClasses} pl-2`}>
            <div className="flex items-center space-x-2">
              {afDetail.twitterProfilePicture && (
                <img 
                  src={afDetail.twitterProfilePicture} 
                  alt={afDetail.twitterName || 'Twitter'} 
                  className="w-6 h-6 rounded-full border border-gray-600 flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div className="flex flex-col min-w-0 flex-1">
                {afDetail.twitterName && (
                  <button
                    onClick={(e) => {
                    //   e.stopPropagation();
                    //   if (twitterUrl) {
                    //     window.open(twitterUrl, '_blank');
                    //   }
                    }}
                    className="text-xs text-blue-400 font-medium hover:text-blue-300 hover:underline truncate text-left"
                    title={afDetail.twitterName}
                  >
                    @{afDetail.twitterName}
                  </button>
                )}
                {afDetail.twitterFollowers && afDetail.twitterFollowers > 0 && (
                  <span className="text-xs text-gray-400 truncate">
                    {afDetail.twitterFollowers >= 1000 
                      ? `${(afDetail.twitterFollowers / 1000).toFixed(1)}K`
                      : afDetail.twitterFollowers
                    } +
                  </span>
                )}
              </div>
            </div>
          </div>
        );

      case 'identity':
        const afDetailForIdentity = getAfDetail(address);
        if (!afDetailForIdentity?.identity || afDetailForIdentity.identity === 'TRADER') {
          return <div className={baseClasses}>-</div>;
        }
        return (
          <div className={baseClasses}>
            <span className="text-[10px] text-gray-300 px-2 py-1 bg-gray-700 rounded">
              {afDetailForIdentity.identity}
            </span>
          </div>
        );

      case 'pattern':
        const afDetailForPattern = getAfDetail(address);
        if (!afDetailForPattern?.pattern || afDetailForPattern.pattern.length === 0) {
          return <div className={baseClasses}>-</div>;
        }
        // NORMAL_TRADING 필터링
        const filteredPatterns = afDetailForPattern.pattern.filter(p => p !== 'NORMAL_TRADING');
        if (filteredPatterns.length === 0) {
          return <div className={baseClasses}>-</div>;
        }
        return (
          <div className={baseClasses}>
            <div className="flex items-center justify-center space-x-1 flex-wrap">
              {filteredPatterns.slice(0, 3).map((p, idx) => (
                <span key={idx} className="text-[10px] text-purple-400 px-1.5 py-0.5 bg-purple-900/30 rounded">
                  {p}
                </span>
              ))}
              {filteredPatterns.length > 3 && (
                <span className="text-[10px] text-gray-500">+{filteredPatterns.length - 3}</span>
              )}
            </div>
          </div>
        );

      case 'performance':
        const afDetailForPerformance = getAfDetail(address);
        if (!afDetailForPerformance?.performance) return <div className={baseClasses}>-</div>;
        return (
          <div className={baseClasses}>
            <span className={`text-[10px] px-2 py-1 rounded ${
              afDetailForPerformance.performance.toLowerCase().includes('high') || 
              afDetailForPerformance.performance.toLowerCase().includes('top')
                ? 'text-green-400 bg-green-900/30'
                : afDetailForPerformance.performance.toLowerCase().includes('low') || 
                  afDetailForPerformance.performance.toLowerCase().includes('poor')
                ? 'text-red-400 bg-red-900/30'
                : 'text-yellow-400 bg-yellow-900/30'
            }`}>
              {afDetailForPerformance.performance}
            </span>
          </div>
        );

      case 'tokenAmount':
        return (
          <div className={baseClasses}>
            <span className="text-gray-400 text-xs">
              {Number(tokenAmount.toFixed(0)).toLocaleString()}
            </span>
          </div>
        );


      case 'avgBuyMC':
        return (
          <div className={baseClasses}>
            <span className="text-gray-400 text-xs">
              {Number(((avgBuyMC || 0) / 1000).toFixed(0)).toLocaleString()}K
            </span>
          </div>
        );

      case 'netBettingRank':
        return (
          <div className={baseClasses}>
            {netBettingRank ? (
              <div className="text-gray-200 text-xs text-center">
                <div className="flex items-center justify-center">

                  {(() => {
                    const rankPercentage = (netBettingRank.rank / netBettingRank.totalCnt) * 100;
                    
                    // 퍼센테이지에 따른 그라데이션 색상 결정
                    let rankColor = 'text-gray-400'; // 기본 회색
                    
                    if (rankPercentage <= 5) {
                      rankColor = 'text-green-600 font-extrabold text-base'; // 매우 진한 초록 (상위 5%)
                    } else if (rankPercentage <= 10) {
                      rankColor = 'text-green-600 font-extrabold'; // 진한 초록 (상위 10%)
                    } else if (rankPercentage <= 20) {
                      rankColor = 'text-green-400'; // 초록 (상위 20%)
                    } else if (rankPercentage <= 30) {
                      rankColor = 'text-green-300'; // 연한 초록 (상위 30%)
                    } else if (rankPercentage <= 50) {
                      rankColor = 'text-gray-400'; // 노란색 (상위 50%)
                    } else if (rankPercentage <= 70) {
                      rankColor = 'text-gray-400'; // 주황색 (상위 70%)
                    } else if (rankPercentage <= 85) {
                      rankColor = 'text-red-400'; // 연한 빨강 (상위 85%)
                    } else if (rankPercentage <= 95) {
                      rankColor = 'text-red-500'; // 빨강 (상위 95%)
                    } else {
                      rankColor = 'text-red-700'; // 진한 빨강 (하위 5%)
                    }
                    
                    return (
                      <div className={`text-sm ${rankColor}`}>
                        {Number(netBettingRank.rank / netBettingRank.totalCnt * 100).toFixed(0)}%
                      </div>
                    );
                  })()}

                </div>
                <div className={`text-gray-400 text-xs`} style={{ fontSize: 10 }} >
                #{netBettingRank.rank} / {netBettingRank.totalCnt} (${Number(netBettingRank.value.toFixed(0)).toLocaleString()})
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'avgHoldingTime':
        return (
          <div className={`${baseClasses} text-sm`}>
            {evaluationData?.metrics?.avg_holding_time_in_min ? (
              <div className="text-gray-300">
                {formatMinutesToLargestUnit(evaluationData.metrics.avg_holding_time_in_min)}
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'pnl':
        return (
          <div className={baseClasses}>
            {evaluationData?.metrics?.pnl !== undefined ? (
              <div className={`text-sm ${evaluationData.metrics.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                <span className={evaluationData.metrics.pnl >= 100000 ? 'text-green-400' : 'text-gray-300'}>
                  {evaluationData.metrics.pnl < 0 ? '-' : ''}${Math.abs(Number(evaluationData.metrics.pnl.toFixed(0))).toLocaleString()}
                </span>
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'winRate':
        return (
          <div className={baseClasses}>
            {evaluationData?.metrics?.win_rate !== undefined ? (
              <div className="text-gray-300 text-sm">
                {evaluationData.metrics.win_rate.toFixed(0)}%
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'scalpingRatio':
        return (
          <div className={baseClasses}>
            {evaluationData?.metrics?.scalping_cnt !== undefined && evaluationData?.metrics?.total_invest_cnt !== undefined ? (
              <div className="text-gray-300 text-xs text-center">
                {(() => {
                  const scalpingPercentage = (evaluationData.metrics.scalping_cnt / evaluationData.metrics.total_invest_cnt) * 100;
                  const textColor = scalpingPercentage > 50 ? 'text-red-400' : 'text-white';

                  return (
                    <div className={textColor}>
                      {scalpingPercentage.toFixed(0)}%
                    </div>
                  );
                })()}
                <div className="text-gray-500 text-[10px]">
                  ({evaluationData.metrics.scalping_cnt} / {evaluationData.metrics.total_invest_cnt})
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'mcRange0':
      case 'mcRange1':
      case 'mcRange2':
      case 'mcRange3':
      case 'mcRange4':
        const rangeIndex = parseInt(column.key.replace('mcRange', ''));
        const textColor = getMcRangeTextColor(evaluationData, rangeIndex);
        return (
          <div className={`${baseClasses}`} style={{
            alignSelf: 'stretch',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {evaluationData?.metrics?.mc_range_metrics?.[rangeIndex] ? (
              <div className={`text-xs ${textColor || 'text-gray-100'}`}>
                <div>{evaluationData.metrics.mc_range_metrics[rangeIndex].count}</div>
                <div className={`text-[10px] ${textColor || 'text-gray-100'}`}>
                  ({Number(evaluationData.metrics.mc_range_metrics[rangeIndex].roi).toFixed(1)} <span className="text-[8px]">SOL</span>)
                </div>
              </div>
            ) : (
              <div className="text-gray-500 text-xs">-</div>
            )}
          </div>
        );

      case 'copy':
        return (
          <div className={baseClasses}>
            <div onClick={(e) => e.stopPropagation()}>
              <CopyButton
                text={address}
                size="sm"
                title="주소 복사"
              />
            </div>
          </div>
        );

      default:
        return <div className={baseClasses}>-</div>;
    }
  };

  // 폴링 로직은 TokenDetailModal에서 통합 관리하므로 제거됨

  return (
    <div className="space-y-2">
      {/* 헤더 */}

      <div className="flex justify-between items-center w-full">
        <h3 className="text-sm font-semibold text-white w-full">
          <div className="mb-2">{title}</div>
          {showCount && (
            <div className="flex justify-between w-full">
              <div className="flex space-x-8">
                <div>
                  <span className="font-normal text-gray-400 mr-2">Total AF Count:</span>
                  <span className="font-normal text-white">
                    {addresses.length}
                  </span>
                </div>
                <div>
                  <span className="font-normal text-gray-400 mr-2">Total AF Token Amount:</span>
                  <span className="font-normal text-white">
                    {totalTokenAmount.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div>
                  <span className="font-normal text-gray-400 mr-2">AF Avg MC:</span>
                  <span className="font-normal text-white">
                    ${(averageAvgBuyMC / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}K
                  </span>
                </div>
              </div>
              
              {/* Top 5 통계 */}
              <div className="flex flex-col gap-2 text-xs mt-2">
                {/* 토큰 보유량 Top 5 */}
                <div className="flex items-center gap-3">
                  <span className="font-normal text-gray-400">Token Amount Top {top5ByTokenAmount.avgNetRank !== null || top5ByTokenAmount.avgBuyMC !== null ? Math.min(5, addresses.length) : 0}:</span>
                  {top5ByTokenAmount.avgNetRank !== null && (
                    <span className="font-normal text-blue-300">
                      Avg Net Rank {top5ByTokenAmount.avgNetRank.toFixed(2)}%
                    </span>
                  )}
                  {top5ByTokenAmount.avgBuyMC !== null && (
                    <span className="font-normal text-white">
                      Avg Entry MC ${(top5ByTokenAmount.avgBuyMC / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}K
                    </span>
                  )}
                </div>
                
                {/* PnL Top 5 */}
                <div className="flex items-center gap-3">
                  <span className="font-normal text-gray-400">PnL Top {top5ByPnL.avgNetRank !== null || top5ByPnL.avgBuyMC !== null ? Math.min(5, addresses.length) : 0}:</span>
                  {top5ByPnL.avgNetRank !== null && (
                    <span className="font-normal text-green-300">
                      Avg Net Rank {top5ByPnL.avgNetRank.toFixed(2)}%
                    </span>
                  )}
                  {top5ByPnL.avgBuyMC !== null && (
                    <span className="font-normal text-white">
                      Avg Entry MC ${(top5ByPnL.avgBuyMC / 1000).toLocaleString(undefined, { maximumFractionDigits: 0 })}K
                    </span>
                  )}
                </div>
              </div>
              {isLoading && (
                <div className="text-yellow-400 text-sm text-center">
                  <span>Loading...</span>
                </div>
              )}
            </div>
          )}
        </h3>
      </div>


      {/* 컬럼 헤더 */}
      <div className="flex justify-between items-center py-1 px-2 text-sm text-gray-400 border-b border-gray-600 mb-2">
        {COLUMNS.map((column) => (
          <div
            key={column.key}
            className={`${column.width} text-${column.align} ${
              column.sortable ? 'cursor-pointer hover:text-white transition-colors' : ''
            }`}
            onClick={() => column.sortable && handleHeaderClick(column.key)}
          >
            <div className="flex items-center justify-center gap-1">
              <span>{column.label}</span>
              {column.sortable && sortColumn === column.key && (
                <span className="text-blue-400">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>


      <div className="space-y-1 text-xs">
        {addresses.length > 0 ? (
          <div className={`${maxHeight} overflow-y-auto border border-gray-700 rounded p-2 bg-gray-900`}>
            {sortedAddresses.map((address, index) => {
              // AF 스토어에서 해당 주소의 데이터 조회 (현재 사용하지 않음)
              // const afData = getAFByAddress(address);

              // addressTokenAmounts에서 해당 주소의 보유량 찾기
              const addressTokenAmount = addressTokenAmounts.find(
                item => item.address === address
              );
              const tokenAmount = addressTokenAmount?.tokenAmount || 0;

              const avgBuyMC = getAvgBuyPrice(address);

              // NetBettingRank 정보 가져오기
              const netBettingRank = getNetBettingRank(address);

              // Evaluation 데이터 가져오기
              const evaluationData = getEvaluationData(address);

              return (
                <div key={index}>
                  <div 
                    className={`flex justify-between items-center border-b border-gray-700 last:border-b-0 cursor-pointer hover:bg-gray-800 transition-colors ${
                      expandedAddress === address ? 'bg-gray-800' : ''
                    }`} 
                    style={{
                      minHeight: 44,
                    }}
                    onClick={() => handleRowClick(address)}
                  >
                    {COLUMNS.map((column) =>
                      renderColumnData(column, address, tokenAmount, avgBuyMC, netBettingRank, evaluationData)
                    )}
                  </div>
                  {expandedAddress === address && renderExpandedContent()}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-gray-400 text-center py-4">
            No addresses found.
          </div>
        )}
      </div>
    </div>
  );
};

export default AFList;
